import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDebouncedCallback } from 'use-debounce';

import styles from './physicalCards.module.scss';
import { actions } from '../../state';
import { config } from '../../api/config';
import {
  formatDate,
  formatMoney,
  mapPhysicalCardBlocked,
  mapPhysicalCardBlockedColor,
} from '../../utils';
import {
  Header,
  Breadcrumb,
  Button,
  Paper,
  PaperContainer,
  TitleSecondary,
} from '../../components';
import Form from './physicalCards.form';
import Usage from './physicalCards.usage.component';
import TopUp from './physicalCards.topUp.component';
import Discounts from './physicalCards.discounts.component';
import {
  cardToFormValues,
  isPhysicalCardFormEqual,
  MIN_OWNER_SEARCH_LENGTH,
  OWNER_TYPE_OPTIONS,
  searchOwnersByType,
  STATUS_OPTIONS,
} from './physicalCards.helpers';
import {
  hasValidationErrors,
  validatePhysicalCardForm,
} from './physicalCards.validation';

const formatOptionalDate = value => (value ? formatDate(value) : '-');

const EditPhysicalCard = ({
  dispatch,
  match: { params },
  physicalCards: { selectedCard, loading },
}) => {
  const [values, setValues] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const [errors, setErrors] = useState({});
  const [ownerResults, setOwnerResults] = useState([]);
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false);
  const [ownerSearchError, setOwnerSearchError] = useState('');
  const [prefillKey, setPrefillKey] = useState(null);

  const clearCard = useCallback(() => {
    dispatch(actions.physicalCards.clearPhysicalCardAction());
    dispatch(actions.physicalCards.clearPhysicalCardUsageAction());
  }, [dispatch]);

  useEffect(() => {
    dispatch(actions.physicalCards.getPhysicalCardAction(params.id));
    return () => {
      clearCard();
    };
  }, [dispatch, clearCard, params.id]);

  useEffect(() => {
    if (!selectedCard || selectedCard.id == null) return;

    const syncKey = selectedCard._formSyncAt || selectedCard.id;
    if (prefillKey === syncKey) return;

    const nextValues = cardToFormValues(selectedCard);
    setValues(nextValues);
    setInitialValues(nextValues);
    setPrefillKey(syncKey);
    setErrors({});
  }, [selectedCard, prefillKey]);

  const formValues = useMemo(() => {
    if (!values) return null;
    return {
      ...values,
      statusOptions: STATUS_OPTIONS,
      ownerTypeOptions: OWNER_TYPE_OPTIONS,
    };
  }, [values]);

  const isDirty = useMemo(
    () => !isPhysicalCardFormEqual(values, initialValues),
    [values, initialValues]
  );

  const liveErrors = useMemo(
    () => (values ? validatePhysicalCardForm(values, { mode: 'edit' }) : {}),
    [values]
  );

  const isSubmitDisabled = useMemo(
    () => !values || !isDirty || hasValidationErrors(liveErrors),
    [values, isDirty, liveErrors]
  );

  const displayErrors = useMemo(() => ({ ...errors, ...liveErrors }), [
    errors,
    liveErrors,
  ]);

  const infoRows = useMemo(() => {
    if (!selectedCard) return [];

    return [
      { title: 'ID', value: selectedCard.id },
      { title: 'RFID', value: selectedCard.number || '-' },
      {
        title: 'Blokuota',
        value: (
          <span
            style={{ color: mapPhysicalCardBlockedColor(selectedCard.blocked) }}
          >
            {mapPhysicalCardBlocked(selectedCard.blocked)}
          </span>
        ),
      },
      {
        title: 'Sukurta',
        value: formatOptionalDate(selectedCard.created_at),
      },
      {
        title: 'Aktyvuota',
        value: formatOptionalDate(selectedCard.activated_at),
      },
      {
        title: 'Paskutinis naudojimas',
        value: formatOptionalDate(selectedCard.last_use_at),
      },
      {
        title: 'Paskutinis papildymas',
        value: formatOptionalDate(selectedCard.last_top_up_date),
      },
      {
        title: 'Likutis',
        value: formatMoney(selectedCard.current_balance || 0),
      },
      {
        title: 'Pridėjimo būdas',
        value:
          selectedCard.import_batch_id == null ? 'Pavienė' : 'Masinis importas',
      },
    ];
  }, [selectedCard]);

  const debouncedOwnerSearch = useDebouncedCallback(
    async (query, ownerType) => {
      const trimmed = (query || '').trim();

      if (trimmed.length < MIN_OWNER_SEARCH_LENGTH) {
        setOwnerResults([]);
        setOwnerSearchError('');
        setOwnerSearchLoading(false);
        return;
      }

      setOwnerSearchLoading(true);
      setOwnerSearchError('');

      try {
        const results = await searchOwnersByType(trimmed, ownerType);
        setOwnerResults(results);
      } catch (e) {
        setOwnerResults([]);
        setOwnerSearchError(e.error ? e.error.error : e.message || 'Klaida');
      } finally {
        setOwnerSearchLoading(false);
      }
    },
    800
  );

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === 'owner_type') {
      const ownerType = Number(value);
      setValues(prev => ({
        ...prev,
        owner_type: ownerType,
        owner_id: null,
        ownerQuery: '',
      }));
      setOwnerResults([]);
      setOwnerSearchError('');
      setErrors(prev => {
        const next = { ...prev };
        delete next.owner;
        delete next.owner_type;
        return next;
      });
      return;
    }

    if (name === 'status') {
      setValues(prev => ({ ...prev, status: Number(value) }));
      setErrors(prev => {
        const next = { ...prev };
        delete next.status;
        return next;
      });
      return;
    }

    setValues(prev => ({ ...prev, [name]: value }));
    setErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleOwnerQueryChange = ({ target: { value } }) => {
    setValues(prev => ({
      ...prev,
      ownerQuery: value,
      owner_id: null,
    }));
    setErrors(prev => {
      const next = { ...prev };
      delete next.owner;
      return next;
    });
    debouncedOwnerSearch(value, values.owner_type);
  };

  const handleOwnerSelect = ownerId => {
    const selected = ownerResults.find(
      result => String(result.value) === String(ownerId)
    );

    setValues(prev => ({
      ...prev,
      owner_id: Number(ownerId),
      ownerQuery: selected ? selected.label : prev.ownerQuery,
    }));
    setOwnerResults([]);
    setErrors(prev => {
      const next = { ...prev };
      delete next.owner;
      return next;
    });
  };

  const buildPatchBody = formState => ({
    status: Number(formState.status),
    owner_type: Number(formState.owner_type),
    owner_id: Number(formState.owner_id),
  });

  const handleSubmit = e => {
    e.preventDefault();
    if (!values || !selectedCard) return;

    const nextErrors = validatePhysicalCardForm(values, { mode: 'edit' });
    setErrors(nextErrors);

    if (hasValidationErrors(nextErrors)) {
      return;
    }

    dispatch(
      actions.physicalCards.updatePhysicalCardAction(
        selectedCard.id,
        buildPatchBody(values)
      )
    );
  };

  const isBlocked = Boolean(selectedCard && selectedCard.blocked);

  const openBlockModal = () => {
    if (!selectedCard) return;

    const nextBlocked = !isBlocked;
    const payload = {
      modalName: 'confirmModal',
      title: nextBlocked ? 'Blokuoti kortelę' : 'Atblokuoti kortelę',
      desc: nextBlocked
        ? 'Ar tikrai norite blokuoti šią kortelę?'
        : 'Ar tikrai norite atblokuoti šią kortelę?',
      onConfirm: () => {
        dispatch(
          actions.physicalCards.updatePhysicalCardAction(selectedCard.id, {
            blocked: nextBlocked,
          })
        );
      },
    };

    dispatch(actions.modals.clearModals());
    dispatch(actions.modals.setModalVisible(payload));
  };

  return (
    <section>
      <Header size="sm">
        <Breadcrumb
          title={config.PAGES.physicalCards}
          subtitle="Redaguoti kortelę"
          baseUrl={config.APP_PATHS.physicalCards}
        />
        <Button
          color="dim"
          disabled={!selectedCard || loading}
          onClick={openBlockModal}
        >
          {isBlocked ? 'Atblokuoti kortelę' : 'Blokuoti kortelę'}
        </Button>
      </Header>

      <PaperContainer className={styles.paperContainer}>
        <Paper className={styles.paperContent}>
          <div className={styles.cardInfoSection}>
            <TitleSecondary>Kortelės duomenys</TitleSecondary>
            {formValues && (
              <Form
                mode="edit"
                values={formValues}
                errors={displayErrors}
                onChange={handleInputChange}
                onOwnerQueryChange={handleOwnerQueryChange}
                onOwnerSelect={handleOwnerSelect}
                onSubmit={handleSubmit}
                loading={loading}
                submitDisabled={isSubmitDisabled}
                ownerResults={ownerResults}
                ownerSearchLoading={ownerSearchLoading}
                ownerSearchError={ownerSearchError}
                infoRows={infoRows}
              />
            )}
          </div>
        </Paper>

        <div className={styles.rightColumn}>
          <Paper className={styles.paperContent}>
            <TopUp
              cardId={params.id}
              currentBalance={
                selectedCard ? selectedCard.current_balance || 0 : 0
              }
              cardStatus={selectedCard ? selectedCard.status : null}
              formSyncAt={selectedCard ? selectedCard._formSyncAt : null}
              loading={loading}
            />
          </Paper>

          <Paper className={styles.paperContent}>
            <Discounts
              cardId={params.id}
              assignments={
                selectedCard ? selectedCard.discount_assignments || [] : []
              }
            />
          </Paper>
        </div>

        <Paper className={cn(styles.paperContent, styles.contentWithFullWidth)}>
          <Usage cardId={params.id} />
        </Paper>
      </PaperContainer>
    </section>
  );
};

const mapStateToProps = state => ({
  physicalCards: state.physicalCards,
});

EditPhysicalCard.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
  }).isRequired,
  physicalCards: PropTypes.shape({
    selectedCard: PropTypes.object,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(EditPhysicalCard);
